/**
 * One-shot "has the user touched the page yet?" probe for the boot splash's
 * stuck/recovered reports (`boot.userInteracted`, SplashScreenLoader.tsx).
 *
 * It separates a user actively waiting on a wedged splash (tapping, pressing
 * keys) from a tab nobody was looking at — the CUSTOMER-UI-3Z triage question.
 * It is a tag only: nothing is muted or skipped on its value.
 *
 * Listens in the capture phase so a handler that stops propagation can't hide
 * the interaction, and removes both listeners after the first hit.
 */
export interface FirstUserInteractionProbe {
  hasInteracted: () => boolean;
  dispose: () => void;
}

const INTERACTION_EVENTS = ["pointerdown", "keydown"] as const;

export function watchFirstUserInteraction(
  target: Pick<EventTarget, "addEventListener" | "removeEventListener">
): FirstUserInteractionProbe {
  let interacted = false;
  const options: AddEventListenerOptions = { capture: true, passive: true };

  const dispose = (): void => {
    for (const type of INTERACTION_EVENTS) {
      target.removeEventListener(type, onInteraction, options);
    }
  };

  function onInteraction(): void {
    interacted = true;
    dispose();
  }

  for (const type of INTERACTION_EVENTS) {
    target.addEventListener(type, onInteraction, options);
  }

  return { hasInteracted: () => interacted, dispose };
}
